import { useEffect, useMemo, useState } from 'react'
import {
  Archive,
  CheckCircle2,
  Edit3,
  ExternalLink,
  FolderOpen,
  LibraryBig,
  Link2,
  Loader2,
  PackageCheck,
  Rocket,
  Search,
  Share2,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react'
import type {
  CogoXyzLibraryItem,
  CogoXyzSourceType,
  CogoXyzTarget,
} from '@cogo/types-v2'
import type { LoadAppPayload } from '@cogo/iframe-protocol'
import { PageHeader } from '@/components/page-header'
import { AppThumbnailPreview } from '@/features/project/components/app-thumbnail-preview'
import {
  cogoXyzRegistry,
  type CogoXyzRegistrySnapshot,
} from '@/features/cogo-xyz/cogo-xyz-registry'
import { loadCogoXyzRegistryFromProjects } from '@/features/cogo-xyz/cogo-xyz-project-storage'
import {
  isPreviewShareActive,
  previewReviewStore,
} from '@/features/preview-review/preview-review-store'
import type {
  AcceptedChangeSet,
  PreviewReviewSnapshot,
  PreviewShare,
  PreviewShareRole,
  ReviewItem,
} from '@/features/preview-review/preview-review-store'
import {
  createPlatformPreviewShare,
  platformRoleForPreviewShare,
  savePreviewReviewSnapshotToServer,
} from '@/features/preview-review/preview-review-server-sync'
import { buildAppPatchesFromReviewPatches } from '@/features/preview-review/review-patch-to-app-patch'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { applyAppPatch } from '@/mcp/app-patch-tool'
import { pushStudioPath, pushStudioRoute } from '@/lib/studio-navigation'
import { useStudioStore } from '@/store/studio-store'
import type { LoadedApp } from '@/features/page-editor/store/types'
import { DashboardContainer } from '../components/dashboard-container'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { AskCogoSurfaceButton } from '@/shell/ide-v2/components/ask-cogo-surface-button'
import { libraryItemSourceProjectId } from './library-project-association'

type ReadinessState = 'ready' | 'pending' | 'missing'

type LibraryToolFeedback = {
  progress: string
  success: string
  failure: string
}

interface ReadinessItem {
  label: string
  value: string
  detail: string
  state: ReadinessState
}

interface CapabilityItem {
  label: string
  detail: string
  state: ReadinessState
}

interface AssetQualityItem {
  label: string
  value: string
  state: ReadinessState
}

const SOURCE_LABELS: Record<CogoXyzSourceType, string> = {
  github_pr: 'Pull request',
  github_repo: 'Repository',
  github_commit: 'Commit',
  figma_frame: 'Figma',
  ide_file: 'Screen file',
  assistant_prompt: 'Brief',
  url_import: 'URL',
  template_remix: 'Library remix',
  manual: 'Manual',
  manual_upload: 'Upload',
}

const TARGET_LABELS: Record<
  CogoXyzTarget | 'report' | 'publishing' | 'game' | 'unknown',
  string
> = {
  landing_page: 'Landing page',
  dashboard: 'Dashboard',
  admin: 'Admin tool',
  creator_page: 'Creator page',
  app: 'Application',
  report: 'Report / document',
  publishing: 'Publishing package',
  game: 'Game / interactive',
  unknown: 'Application',
}

const LIBRARY_RELATIONSHIP_COPY = {
  en: {
    summary: 'Library contains saved, reusable artifacts. Projects are editable workspaces, so the two totals are not expected to match. Counts below describe records loaded in this session.',
    items: (count: number) => `${count} loaded ${count === 1 ? 'item' : 'items'}`,
    workspaces: (count: number) => `${count} source ${count === 1 ? 'workspace' : 'workspaces'}`,
    unlinked: (count: number) => `${count} without a matching project`,
    sourceWorkspace: (name: string) => `Source workspace: ${name}`,
    sourceUnavailable: 'Source workspace is not available in the current project list',
    emptyCounts: (projects: number) => `${projects} editable workspaces · 0 Library items loaded`,
  },
  ko: {
    summary: 'Library는 저장된 재사용 결과물입니다. 프로젝트는 편집 가능한 워크스페이스이므로 두 숫자가 같을 필요가 없습니다. 아래 숫자는 이 세션에서 읽은 기록입니다.',
    items: (count: number) => `읽은 항목 ${count}개`,
    workspaces: (count: number) => `소스 워크스페이스 ${count}개`,
    unlinked: (count: number) => `프로젝트 연결 없음 ${count}개`,
    sourceWorkspace: (name: string) => `소스 워크스페이스: ${name}`,
    sourceUnavailable: '현재 프로젝트 목록에서 소스 워크스페이스를 확인할 수 없음',
    emptyCounts: (projects: number) => `편집 가능한 워크스페이스 ${projects}개 · 읽은 Library 항목 0개`,
  },
  ja: {
    summary: 'Library は保存済みの再利用可能な成果物です。プロジェクトは編集可能なワークスペースなので、2つの合計が一致する必要はありません。以下はこのセッションで読み込んだ記録です。',
    items: (count: number) => `読み込み済み項目 ${count} 件`,
    workspaces: (count: number) => `ソースワークスペース ${count} 件`,
    unlinked: (count: number) => `プロジェクト未接続 ${count} 件`,
    sourceWorkspace: (name: string) => `ソースワークスペース: ${name}`,
    sourceUnavailable: '現在のプロジェクト一覧ではソースワークスペースを確認できません',
    emptyCounts: (projects: number) => `編集可能なワークスペース ${projects} 件 · 読み込み済み Library 項目 0 件`,
  },
  zh: {
    summary: 'Library 包含已保存、可复用的成果。项目是可编辑工作区，因此两者数量不必相同。以下数字仅描述本次会话已读取的记录。',
    items: (count: number) => `${count} 个已读取项目`,
    workspaces: (count: number) => `${count} 个来源工作区`,
    unlinked: (count: number) => `${count} 个未关联项目`,
    sourceWorkspace: (name: string) => `来源工作区：${name}`,
    sourceUnavailable: '当前项目列表中找不到来源工作区',
    emptyCounts: (projects: number) => `${projects} 个可编辑工作区 · 0 个已读取 Library 项目`,
  },
  'zh-TW': {
    summary: 'Library 包含已儲存、可重用的成果。專案是可編輯工作區，因此兩者數量不必相同。以下數字僅描述本次工作階段已讀取的記錄。',
    items: (count: number) => `${count} 個已讀取項目`,
    workspaces: (count: number) => `${count} 個來源工作區`,
    unlinked: (count: number) => `${count} 個未連結專案`,
    sourceWorkspace: (name: string) => `來源工作區：${name}`,
    sourceUnavailable: '目前的專案清單中找不到來源工作區',
    emptyCounts: (projects: number) => `${projects} 個可編輯工作區 · 0 個已讀取 Library 項目`,
  },
} as const

function itemTitle(item: CogoXyzLibraryItem): string {
  return item.workItem.title || item.workItem.id
}

function sourceLabel(type: CogoXyzSourceType): string {
  return SOURCE_LABELS[type] ?? type
}

function proofClass(level: string): string {
  if (level === 'failed')
    return 'border border-destructive/30 bg-background text-destructive'
  return 'border border-border bg-background text-muted-foreground'
}

function readinessDotClass(state: ReadinessState): string {
  if (state === 'ready') return 'bg-foreground'
  if (state === 'pending') return 'bg-muted-foreground'
  return 'bg-destructive'
}

/**
 * The IDE workspace path for a saved library item, or `null` when the item has
 * no workspace to return to.
 *
 * Library previews are registered as `${origin}/ide/<projectId>` (with a
 * `?surface=preview` query on registry-created previews) — see
 * `cogo-xyz-import.ts`, `cogo-xyz-prompt-generation.ts`,
 * `cogo-xyz-template-gallery.ts`. The workspace target is therefore that same
 * `/ide/<projectId>` path WITHOUT the preview query, which is what
 * "Return to the project workspace" means. Cross-origin or non-`/ide/` preview
 * URLs (an externally hosted deployment, say) have no workspace, so the caller
 * must disable the control instead of pretending it can navigate.
 */
export function libraryItemWorkspacePath(
  item: Pick<CogoXyzLibraryItem, 'preview'> | null | undefined,
): string | null {
  const previewUrl = item?.preview?.previewUrl
  if (!previewUrl) return null
  const origin =
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin
  let parsed: URL
  try {
    parsed = new URL(previewUrl, origin)
  } catch {
    return null
  }
  if (parsed.origin !== origin) return null
  if (!parsed.pathname.startsWith('/ide/')) return null
  const projectId = parsed.pathname.slice('/ide/'.length).split('/')[0]
  return projectId ? `/ide/${projectId}` : null
}

function libraryStatusLabel(level: string): string {
  if (level === 'failed') return 'Needs review'
  if (level === 'deployed') return 'Launched'
  if (level === 'attached') return 'Shared'
  if (level === 'previewed') return 'Ready to open'
  if (level === 'inspected') return 'Checked'
  return 'Draft'
}

function readinessClass(state: ReadinessState): string {
  if (state === 'ready')
    return 'border-border bg-background text-muted-foreground'
  if (state === 'pending')
    return 'border-border bg-background text-muted-foreground'
  return 'border-destructive/30 bg-background text-destructive'
}

const MANAGEABLE_REVIEW_SHARE_ROLES: PreviewShareRole[] = [
  'view',
  'comment',
  'suggest',
]

function reviewShareState(
  share: PreviewShare,
): 'active' | 'expired' | 'revoked' {
  if (share.revokedAt) return 'revoked'
  return isPreviewShareActive(share) ? 'active' : 'expired'
}

function reviewShareStateClass(share: PreviewShare): string {
  const state = reviewShareState(share)
  if (state === 'active')
    return 'border-border bg-background text-muted-foreground'
  if (state === 'expired')
    return 'border-border bg-background text-muted-foreground'
  return 'border-destructive/30 bg-background text-destructive'
}

function reviewShareRoleOptions(share: PreviewShare): PreviewShareRole[] {
  return MANAGEABLE_REVIEW_SHARE_ROLES.includes(share.role)
    ? MANAGEABLE_REVIEW_SHARE_ROLES
    : [share.role, ...MANAGEABLE_REVIEW_SHARE_ROLES]
}

function shortTokenHash(share: PreviewShare): string {
  return share.tokenHash.split(':').at(-1)?.slice(-8) ?? share.id
}

function shortDate(value: string): string {
  return new Date(value).toLocaleDateString('ko-KR')
}

function hostLabel(value: string): string {
  try {
    return new URL(value).host
  } catch {
    return value
  }
}

function slugFromPreviewUrl(value: string | undefined): string | null {
  if (!value) return null
  try {
    const host = new URL(value).host
    return host.endsWith('.cogo.xyz') ? host.replace(/\.cogo\.xyz$/, '') : null
  } catch {
    return null
  }
}

function metadataString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function metadataNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function metadataRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function itemTarget(item: CogoXyzLibraryItem): keyof typeof TARGET_LABELS {
  const target = metadataString(item.workItem.sourceRef.metadata?.target)
  if (target && target in TARGET_LABELS)
    return target as keyof typeof TARGET_LABELS
  return 'unknown'
}

function itemTargetLabel(item: CogoXyzLibraryItem): string {
  return TARGET_LABELS[itemTarget(item)]
}

function capabilityItems(
  item: CogoXyzLibraryItem,
  cloudflareDeployCount: number,
): CapabilityItem[] {
  const target = itemTarget(item)
  const capabilities: CapabilityItem[] = [
    {
      label: 'Open',
      detail: item.preview?.previewUrl
        ? 'Ready to open and review'
        : 'Open link is missing',
      state: item.preview?.previewUrl ? 'ready' : 'missing',
    },
    {
      label: 'Saved file',
      detail: item.bundle?.id
        ? 'Workspace file is saved'
        : 'Saved file is missing',
      state: item.bundle?.id ? 'ready' : 'missing',
    },
    {
      label: 'Launch',
      detail:
        cloudflareDeployCount > 0
          ? `${cloudflareDeployCount} launch record saved`
          : 'Prepare launch when this work is ready',
      state: cloudflareDeployCount > 0 ? 'ready' : 'pending',
    },
  ]

  if (target === 'report' || target === 'publishing') {
    capabilities.push({
      label: 'Export',
      detail: 'PDF, DOCX, PPTX, and HWP export can be prepared from here',
      state: 'pending',
    })
  }

  if (target === 'game') {
    capabilities.push({
      label: 'Interactive play',
      detail: 'Animation and play states can be checked before launch',
      state: 'pending',
    })
  }

  return capabilities
}

function assetQualityItems(
  item: CogoXyzLibraryItem,
  capabilities: CapabilityItem[],
): AssetQualityItem[] {
  const readyCapabilityCount = capabilities.filter(
    (capability) => capability.state === 'ready',
  ).length
  return [
    {
      label: 'Visual',
      value: item.preview?.previewUrl ? 'ready' : 'fallback',
      state: 'ready',
    },
    {
      label: 'Type',
      value: itemTargetLabel(item),
      state: 'ready',
    },
    {
      label: 'Open link',
      value: item.preview?.status ?? 'missing',
      state: item.preview?.previewUrl ? 'ready' : 'missing',
    },
    {
      label: 'Details',
      value: `${item.evidenceCount} record${item.evidenceCount === 1 ? '' : 's'}`,
      state: item.evidenceCount > 0 ? 'ready' : 'missing',
    },
    {
      label: 'Ready',
      value: `${readyCapabilityCount}/${capabilities.length}`,
      state:
        readyCapabilityCount === capabilities.length
          ? 'ready'
          : readyCapabilityCount > 0
            ? 'pending'
            : 'missing',
    },
    {
      label: 'Reuse',
      value: 'available',
      state: 'ready',
    },
  ]
}

function cloneJson<T>(value: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : (JSON.parse(JSON.stringify(value)) as T)
}

function loadedAppToPreviewPayload(
  project: LoadedApp | null | undefined,
): LoadAppPayload | undefined {
  if (
    !project?.manifest ||
    !project.blocks ||
    Object.keys(project.blocks).length === 0
  )
    return undefined
  return {
    manifest: cloneJson(project.manifest as unknown as Record<string, unknown>),
    blocks: cloneJson(project.blocks as unknown as Record<string, unknown>),
    designSystem: project.designSystem
      ? cloneJson(project.designSystem)
      : undefined,
  }
}

function currentProjectManifestTitle(
  project: LoadedApp | null | undefined,
): string | undefined {
  const manifest = project?.manifest as
    | { name?: unknown; title?: unknown }
    | undefined
  return metadataString(manifest?.name) ?? metadataString(manifest?.title)
}

function currentProjectMatchesLibraryItem(
  item: CogoXyzLibraryItem,
  currentProjectId: string | null,
  currentProject: LoadedApp | null | undefined,
): boolean {
  const previewUrl = item.preview?.previewUrl ?? ''
  if (currentProjectId) {
    const metadataProjectId = metadataString(
      item.workItem.sourceRef.metadata?.projectId,
    )
    const metadataAppId = metadataString(
      item.workItem.sourceRef.metadata?.appId,
    )
    if (
      previewUrl.includes(encodeURIComponent(currentProjectId)) ||
      previewUrl.includes(currentProjectId) ||
      currentProjectId === item.workItem.id ||
      currentProjectId === metadataProjectId ||
      currentProjectId === metadataAppId
    ) {
      return true
    }
  }

  const projectTitle = currentProjectManifestTitle(currentProject)
  return Boolean(projectTitle && projectTitle === itemTitle(item))
}

function canCreateReviewShare(
  item: CogoXyzLibraryItem,
  currentProjectId: string | null,
  currentProject: LoadedApp | null | undefined,
  currentPreviewPayload: LoadAppPayload | undefined,
): boolean {
  return Boolean(
    item.preview?.previewUrl &&
    hasEmbeddedReviewPayload(
      item,
      currentProjectId,
      currentProject,
      currentPreviewPayload,
    ),
  )
}

function hasEmbeddedReviewPayload(
  item: CogoXyzLibraryItem,
  currentProjectId: string | null,
  currentProject: LoadedApp | null | undefined,
  currentPreviewPayload: LoadAppPayload | undefined,
): boolean {
  return Boolean(
    currentProjectMatchesLibraryItem(item, currentProjectId, currentProject) &&
    currentPreviewPayload,
  )
}

export function LibraryHubPage() {
  const { locale, strings } = useStudioLocale()
  const relationshipCopy = LIBRARY_RELATIONSHIP_COPY[locale]
  const libraryCopy = strings.library
  const [snapshot, setSnapshot] = useState<CogoXyzRegistrySnapshot>(() =>
    cogoXyzRegistry.snapshot(),
  )
  const [reviewSnapshot, setReviewSnapshot] = useState<PreviewReviewSnapshot>(
    () => previewReviewStore.snapshot(),
  )
  const [query, setQuery] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [busyTool, setBusyTool] = useState<string | null>(null)
  const [message, setMessage] = useState('Library is ready.')
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const userProjects = useStudioStore((s) => s.userProjects)
  const loadUserProjects = useStudioStore((s) => s.loadUserProjects)
  const isLoadingUserProjects = useStudioStore((s) => s.isLoadingUserProjects)
  const userProjectsLoadedAt = useStudioStore((s) => s.userProjectsLoadedAt)
  const userProjectsError = useStudioStore((s) => s.userProjectsError)
  const projectRelationshipsReady =
    userProjectsLoadedAt !== null &&
    !isLoadingUserProjects &&
    !userProjectsError
  const currentPreviewPayload = useMemo(
    () => loadedAppToPreviewPayload(currentProject as LoadedApp | null),
    [currentProject],
  )

  function refresh(nextSelectedId?: string) {
    const next = cogoXyzRegistry.snapshot()
    setSnapshot(next)
    setReviewSnapshot(previewReviewStore.snapshot())
    const items = cogoXyzRegistry.listLibraryItems({
      includeArchived: true,
    }).items
    setSelectedId(nextSelectedId ?? selectedId ?? items[0]?.workItem.id ?? null)
  }

  useEffect(() => {
    let mounted = true
    void (async () => {
      await loadUserProjects()
      const state = useStudioStore.getState()
      const projectIds = [
        ...state.userProjects.map((project) => project.id),
        ...(state.currentProjectId ? [state.currentProjectId] : []),
      ]
      await loadCogoXyzRegistryFromProjects([...new Set(projectIds)])
    })()
      .catch((error) => {
        console.warn('[Library] failed to load workspace project snapshots', error)
      })
      .finally(() => {
        if (mounted) refresh()
      })
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUserProjects])

  const allLibraryItems = useMemo(() => {
    return cogoXyzRegistry.listLibraryItems({ includeArchived: true }).items
    // snapshot is included so registry mutations trigger recomputation.
  }, [snapshot])
  const libraryItems = useMemo(() => {
    return cogoXyzRegistry.listLibraryItems({
      includeArchived: true,
      query: query.trim() || undefined,
    }).items
    // snapshot is included so registry mutations trigger recomputation.
  }, [query, snapshot])
  const projectById = useMemo(
    () => new Map(userProjects.map((project) => [project.id, project])),
    [userProjects],
  )
  const projectIds = useMemo(() => new Set(projectById.keys()), [projectById])
  const sourceProjectIdByItem = useMemo(() => {
    return new Map(
      allLibraryItems.map((item) => [
        item.workItem.id,
        libraryItemSourceProjectId(item, projectIds),
      ]),
    )
  }, [allLibraryItems, projectIds])
  const activeLibraryItems = allLibraryItems.filter(
    (item) => item.workItem.status !== 'archived',
  )
  const linkedSourceProjectIds = new Set(
    activeLibraryItems
      .map((item) => sourceProjectIdByItem.get(item.workItem.id))
      .filter((id): id is string => Boolean(id)),
  )
  const unlinkedLibraryItemCount = activeLibraryItems.filter(
    (item) => !sourceProjectIdByItem.get(item.workItem.id),
  ).length

  const selectedItem =
    libraryItems.find((item) => item.workItem.id === selectedId) ??
    libraryItems[0] ??
    null
  const packet = selectedItem
    ? cogoXyzRegistry.getEvidencePacket(selectedItem.workItem.id)
    : null
  const deployments = selectedItem
    ? cogoXyzRegistry.listCloudflareDeployments({
        workItemId: selectedItem.workItem.id,
      }).records
    : []
  const latestEvidence = packet?.evidence.at(-1)
  const browserEvidence = packet?.evidence.find(
    (artifact) => artifact.type === 'browser_check',
  )
  const figmaMappingEvidence = packet?.evidence.find(
    (artifact) => artifact.type === 'figma_mapping',
  )
  const figmaOntologyValidation = metadataRecord(
    figmaMappingEvidence?.metadata?.ontologyValidation,
  )
  const figmaOntologyStatus = metadataString(figmaOntologyValidation?.status)
  const figmaOntologyNodeMappings =
    metadataNumber(figmaOntologyValidation?.nodeMappingCount) ?? 0
  const figmaOntologyAdmittedSources =
    metadataNumber(figmaOntologyValidation?.admittedSourceCount) ?? 0
  const figmaOntologyHardConstraints = Array.isArray(
    figmaOntologyValidation?.hardConstraints,
  )
    ? figmaOntologyValidation.hardConstraints.length
    : 0
  const externalHandoffCount =
    (packet?.evidence.filter(
      (artifact) =>
        artifact.type === 'github_comment' ||
        artifact.type === 'github_check' ||
        artifact.type === 'gallery_publish' ||
        artifact.type === 'figma_mapping' ||
        artifact.type === 'deploy_record',
    ).length ?? 0) + deployments.length
  const readinessItems: ReadinessItem[] = selectedItem
    ? [
        {
          label: 'Source',
          value: sourceLabel(selectedItem.workItem.sourceType),
          detail: selectedItem.workItem.sourceRef.label,
          state: 'ready',
        },
        {
          label: 'Bundle',
          value: selectedItem.bundle ? 'Saved file' : 'Missing file',
          detail: selectedItem.bundle?.id ?? 'No saved file for this item',
          state: selectedItem.bundle ? 'ready' : 'missing',
        },
        {
          label: 'Open link',
          value: selectedItem.preview?.status ?? 'Missing link',
          detail: selectedItem.preview?.previewUrl
            ? hostLabel(selectedItem.preview.previewUrl)
            : 'No open link yet',
          state: selectedItem.preview?.previewUrl ? 'ready' : 'missing',
        },
        {
          label: 'Details',
          value: `${packet?.evidence.length ?? 0} record${(packet?.evidence.length ?? 0) === 1 ? '' : 's'}`,
          detail: latestEvidence
            ? `${latestEvidence.type} · ${latestEvidence.status}`
            : 'No detail records yet',
          state: packet?.evidence.length ? 'ready' : 'missing',
        },
        ...(selectedItem.workItem.sourceType === 'figma_frame'
          ? [
              {
                label: 'Design check',
                value: figmaOntologyStatus
                  ? figmaOntologyStatus === 'pass'
                    ? 'Design ready'
                    : 'Needs review'
                  : 'Missing design check',
                detail: figmaOntologyStatus
                  ? `${figmaOntologyAdmittedSources} source part${figmaOntologyAdmittedSources === 1 ? '' : 's'} read · ${figmaOntologyNodeMappings} mapped element${figmaOntologyNodeMappings === 1 ? '' : 's'} · ${figmaOntologyHardConstraints} rule${figmaOntologyHardConstraints === 1 ? '' : 's'} checked`
                  : 'Figma import has no design check yet',
                state:
                  figmaOntologyStatus === 'pass'
                    ? 'ready'
                    : figmaOntologyStatus
                      ? 'pending'
                      : 'missing',
              } satisfies ReadinessItem,
            ]
          : []),
        {
          label: 'Check',
          value: browserEvidence?.status ?? 'Not checked',
          detail:
            browserEvidence?.summary ??
            'Run Check before treating this work as ready',
          state: browserEvidence ? 'ready' : 'pending',
        },
        {
          label: 'Sharing',
          value:
            externalHandoffCount > 0
              ? `${externalHandoffCount} record${externalHandoffCount === 1 ? '' : 's'}`
              : 'Not shared',
          detail:
            externalHandoffCount > 0
              ? 'Gallery, pull request, design, or launch record is attached'
              : 'No share or launch record yet',
          state: externalHandoffCount > 0 ? 'ready' : 'pending',
        },
      ]
    : []
  const selectedCapabilities = selectedItem
    ? capabilityItems(selectedItem, deployments.length)
    : []
  const selectedQualityItems = selectedItem
    ? assetQualityItems(selectedItem, selectedCapabilities)
    : []
  const selectedHasEmbeddedReviewPayload = selectedItem
    ? hasEmbeddedReviewPayload(
        selectedItem,
        currentProjectId,
        currentProject as LoadedApp | null,
        currentPreviewPayload,
      )
    : false
  const selectedReviewPreviewIds = selectedItem
    ? new Set(
        reviewSnapshot.previews
          .filter((preview) => preview.projectId === selectedItem.workItem.id)
          .map((preview) => preview.id),
      )
    : new Set<string>()
  const selectedReviewItems =
    selectedReviewPreviewIds.size > 0
      ? reviewSnapshot.reviewItems.filter((item) =>
          selectedReviewPreviewIds.has(item.previewId),
        )
      : []
  const selectedReviewShares =
    selectedReviewPreviewIds.size > 0
      ? reviewSnapshot.shares.filter((share) =>
          selectedReviewPreviewIds.has(share.previewId),
        )
      : []
  const selectedAcceptedChangeSets = selectedItem
    ? reviewSnapshot.acceptedChangeSets.filter(
        (changeSet) =>
          changeSet.projectId === selectedItem.workItem.id &&
          selectedReviewPreviewIds.has(changeSet.previewId),
      )
    : []
  const counts = {
    items: snapshot.workItems.filter((item) => item.status !== 'archived')
      .length,
    previews: snapshot.previews.length,
    evidence: snapshot.evidence.length,
    deployments: snapshot.evidence.filter(
      (item) => item.type === 'deploy_record',
    ).length,
  }

  async function runLibraryTool(
    label: string,
    tool: string,
    params: Record<string, unknown>,
    feedback?: LibraryToolFeedback,
  ) {
    setBusyTool(label)
    setMessage(feedback?.progress ?? `${label}...`)
    try {
      const result = await mcpBus.execute({ tool, params })
      if (!result.success) {
        setMessage(feedback?.failure ?? result.error ?? `${label} failed.`)
        return
      }
      const workItemId =
        typeof params.workItemId === 'string'
          ? params.workItemId
          : selectedItem?.workItem.id
      refresh(workItemId)
      setMessage(feedback?.success ?? `${label} recorded.`)
    } finally {
      setBusyTool(null)
    }
  }

  async function createReviewShareLink(item: CogoXyzLibraryItem) {
    if (!item.preview?.previewUrl) return
    const appPayload = currentProjectMatchesLibraryItem(
      item,
      currentProjectId,
      currentProject as LoadedApp | null,
    )
      ? currentPreviewPayload
      : undefined
    if (!appPayload) {
      setMessage(libraryCopy.selectedDetails.openBeforeReviewLink)
      return
    }
    setBusyTool('Share review')
    setMessage('Creating review link...')
    try {
      let registryShareRecorded = false
      try {
        const registryShare = await mcpBus.execute({
          tool: 'cogo.studio.share_cogo_preview',
          params: {
            previewUrl: item.preview.previewUrl,
            visibility: 'unlisted',
            createdBy: 'studio',
            registryProjectId: item.workItem.workspaceId,
          },
        })
        registryShareRecorded = Boolean(registryShare?.success)
      } catch {
        registryShareRecorded = false
      }
      const preview = previewReviewStore.createPreviewSnapshot({
        projectId: item.workItem.id,
        projectVersionId: item.bundle?.id ?? 'unversioned',
        title: itemTitle(item),
        appPayload,
        sourcePreviewUrl: item.preview.previewUrl,
        appSpecRef: item.bundle?.bundleRef,
        renderPackRef: item.bundle?.bundleRef,
        sourceTrace: {
          sourceType: item.workItem.sourceType,
          sourceLabel: item.workItem.sourceRef.label,
        },
        evidence: {
          libraryWorkItemId: item.workItem.id,
          bundleId: item.bundle?.id,
          sourcePreviewUrl: item.preview.previewUrl,
          capturedProjectId: currentProjectId,
          appPayloadCaptured: Boolean(appPayload),
        },
        createdBy: 'studio',
      })
      const platformProjectId = currentProjectId ?? item.workItem.id
      const platformShare = await createPlatformPreviewShare({
        projectId: platformProjectId,
        slug: slugFromPreviewUrl(item.preview.previewUrl),
        role: platformRoleForPreviewShare('suggest'),
        evidenceRefs:
          cogoXyzRegistry
            .getEvidencePacket(item.workItem.id)
            ?.evidence.map((artifact) => artifact.id) ?? [],
      })
      const share = previewReviewStore.createPreviewShare({
        previewId: preview.id,
        role: 'suggest',
        visibility: 'unlisted',
        createdBy: 'studio',
        rawToken: platformShare.share?.token,
      })
      const sync = await savePreviewReviewSnapshotToServer(
        preview.id,
        previewReviewStore.snapshot(),
      )
      await navigator.clipboard
        ?.writeText(share.reviewUrl)
        .catch(() => undefined)
      const prefix = registryShareRecorded ? 'Share link recorded. ' : ''
      setMessage(
        sync.ok
          ? `${prefix}Review link ready: ${share.reviewUrl}`
          : `${prefix}Review link ready locally; sync pending: ${share.reviewUrl}`,
      )
      refresh(item.workItem.id)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Review link failed.')
    } finally {
      setBusyTool(null)
    }
  }

  async function resolveReviewItem(item: ReviewItem) {
    previewReviewStore.resolveReviewItem(item.id)
    const sync = await savePreviewReviewSnapshotToServer(
      item.previewId,
      previewReviewStore.snapshot(),
    )
    setMessage(
      sync.ok
        ? 'Review note resolved.'
        : 'Review note resolved locally; sync pending.',
    )
    refresh(selectedItem?.workItem.id)
  }

  async function rejectReviewItem(item: ReviewItem) {
    previewReviewStore.rejectReviewItem(item.id)
    const sync = await savePreviewReviewSnapshotToServer(
      item.previewId,
      previewReviewStore.snapshot(),
    )
    setMessage(
      sync.ok
        ? 'Suggestion rejected.'
        : 'Suggestion rejected locally; sync pending.',
    )
    refresh(selectedItem?.workItem.id)
  }

  async function updateReviewShareRole(
    share: PreviewShare,
    role: PreviewShareRole,
  ) {
    previewReviewStore.updatePreviewShare(share.id, { role })
    const sync = await savePreviewReviewSnapshotToServer(
      share.previewId,
      previewReviewStore.snapshot(),
    )
    setMessage(
      sync.ok
        ? `Review link changed to ${role} access.`
        : `Review link changed to ${role} access locally; sync pending.`,
    )
    refresh(selectedItem?.workItem.id)
  }

  async function revokeReviewShare(share: PreviewShare) {
    previewReviewStore.revokePreviewShare(share.id)
    const sync = await savePreviewReviewSnapshotToServer(
      share.previewId,
      previewReviewStore.snapshot(),
    )
    setMessage(
      sync.ok
        ? 'Review link revoked.'
        : 'Review link revoked locally; sync pending.',
    )
    refresh(selectedItem?.workItem.id)
  }

  async function acceptReviewItem(item: ReviewItem) {
    if (!selectedItem || !item.proposedPatch) return
    const preview = previewReviewStore.getPreviewSnapshot(item.previewId)
    const converted = buildAppPatchesFromReviewPatches(
      [item.proposedPatch],
      preview?.appPayload,
    )
    if (converted.errors.length > 0 || converted.patches.length === 0) {
      setMessage(
        `Cannot apply suggestion automatically: ${converted.errors.join(' ') || 'no patch produced'}`,
      )
      return
    }
    try {
      const patchResult = applyAppPatch({ patches: converted.patches })
      const appliedVersionId = `${selectedItem.bundle?.id ?? selectedItem.workItem.id}-accepted-${item.id}`
      previewReviewStore.acceptReviewItems({
        projectId: selectedItem.workItem.id,
        previewId: item.previewId,
        reviewItemIds: [item.id],
        rollbackPatches: patchResult._inverse.params.patches,
        appliedVersionId,
        rollbackVersionId: selectedItem.bundle?.id ?? 'unversioned',
        acceptedBy: 'studio',
      })
      const sync = await savePreviewReviewSnapshotToServer(
        item.previewId,
        previewReviewStore.snapshot(),
      )
      setMessage(
        sync.ok
          ? 'Review suggestion applied to the open project.'
          : 'Review suggestion applied locally; sync pending.',
      )
      refresh(selectedItem.workItem.id)
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : 'Review suggestion patch failed.',
      )
    }
  }

  async function rollbackAcceptedChangeSet(changeSet: AcceptedChangeSet) {
    if (!selectedItem) return
    if (changeSet.rolledBackAt) {
      setMessage('This accepted change has already been rolled back.')
      return
    }
    if (!changeSet.rollbackPatches?.length) {
      setMessage(
        'This accepted change does not include an automatic rollback patch.',
      )
      return
    }
    try {
      applyAppPatch({ patches: changeSet.rollbackPatches })
      const rollbackAppliedVersionId = `${changeSet.rollbackVersionId}-rollback-${changeSet.id}`
      previewReviewStore.rollbackAcceptedChangeSet(changeSet.id, {
        rollbackAppliedVersionId,
        rolledBackBy: 'studio',
      })
      const sync = await savePreviewReviewSnapshotToServer(
        changeSet.previewId,
        previewReviewStore.snapshot(),
      )
      setMessage(
        sync.ok
          ? `Accepted change rolled back into ${rollbackAppliedVersionId}.`
          : `Accepted change rolled back locally into ${rollbackAppliedVersionId}; server sync pending.`,
      )
      refresh(selectedItem.workItem.id)
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : 'Review rollback failed.',
      )
    }
  }

  function goToImport() {
    setStudioRoute('import')
    pushStudioRoute('import')
  }

  function goToProjects() {
    setStudioRoute('projects')
    pushStudioRoute('projects')
  }

  function focusEvidence(itemId: string) {
    setSelectedId(itemId)
    window.requestAnimationFrame(() => {
      const panel = document.getElementById('library-details-panel')
      const reduceMotion = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches
      if (typeof panel?.scrollIntoView === 'function') {
        panel.scrollIntoView({
          block: 'start',
          behavior: reduceMotion ? 'auto' : 'smooth',
        })
      }
    })
  }

  function prepareDeployFromCard(item: CogoXyzLibraryItem) {
    setSelectedId(item.workItem.id)
    if (!item.bundle?.id) return
    void runLibraryTool(
      libraryCopy.actions.prepareLaunch,
      'cogo.studio.prepare_cogo_deploy',
      {
        bundleId: item.bundle.id,
        provider: 'cloudflare_pages',
        target: 'production',
        approved: false,
        registryProjectId: item.workItem.workspaceId,
      },
      libraryCopy.actions.feedback.prepareLaunch,
    )
  }

  function startRemixFromCard(item: CogoXyzLibraryItem) {
    setSelectedId(item.workItem.id)
    setStudioRoute('import')
    pushStudioPath(
      `/studio/import?remix=${encodeURIComponent(item.workItem.id)}`,
    )
  }

  function publishGalleryFromCard(item: CogoXyzLibraryItem) {
    setSelectedId(item.workItem.id)
    void runLibraryTool(
      libraryCopy.actions.sharePublicly,
      'cogo.studio.publish_cogo_gallery_item',
      {
        workItemId: item.workItem.id,
        visibility: 'public',
        title: itemTitle(item),
        audience: itemTarget(item),
        tags: [itemTarget(item), item.workItem.sourceType],
        createdBy: 'studio-library',
        registryProjectId: item.workItem.workspaceId,
      },
      libraryCopy.actions.feedback.sharePublicly,
    )
  }

  /**
   * Open the saved item's project workspace.
   *
   * `pushStudioPath` fires the popstate that App.tsx's `/ide/` branch consumes
   * (restore-imported / open-server-project), which is the SPA equivalent of
   * following the card's Open link — the one control that was reported as
   * actually reaching the workspace.
   */
  function openItemWorkspace(item: CogoXyzLibraryItem) {
    const path = libraryItemWorkspacePath(item)
    if (!path) return
    setSelectedId(item.workItem.id)
    pushStudioPath(path)
  }

  return (
    <DashboardContainer
      className="p-6 space-y-6 max-w-7xl"
      data-testid="studio-library-page"
    >
      <PageHeader
        route="library"
        title={libraryCopy.title}
        description={libraryCopy.subtitle}
        action={
          <button
            type="button"
            onClick={goToImport}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-foreground px-4 text-sm font-semibold text-background transition-colors hover:opacity-90"
          >
            <Sparkles className="h-4 w-4" />
            {libraryCopy.importSource}
          </button>
        }
      />

      <section
        data-testid="library-project-relationship-summary"
        className="rounded-xl border border-border bg-card px-4 py-3"
      >
        <p className="text-sm leading-6 text-muted-foreground">
          {relationshipCopy.summary}
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full border border-border bg-background px-2.5 py-1 font-semibold text-foreground">
            {relationshipCopy.items(activeLibraryItems.length)}
          </span>
          {projectRelationshipsReady ? (
            <>
              <span className="rounded-full border border-border bg-background px-2.5 py-1 text-muted-foreground">
                {relationshipCopy.workspaces(linkedSourceProjectIds.size)}
              </span>
              {unlinkedLibraryItemCount > 0 ? (
                <span className="rounded-full border border-border bg-background px-2.5 py-1 text-muted-foreground">
                  {relationshipCopy.unlinked(unlinkedLibraryItemCount)}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      <section
        className={
          selectedItem
            ? 'grid gap-5 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]'
            : 'block'
        }
        data-testid="studio-library-content-grid"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={libraryCopy.searchPlaceholder}
              className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
            />
          </div>
          <div
            className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-xs"
            data-testid="studio-library-search-summary"
          >
            <span className="font-medium text-foreground">
              {libraryCopy.searchResults}
            </span>
            <span className="text-muted-foreground">
              {libraryCopy.savedWorkCount.replace(
                '{count}',
                String(libraryItems.length),
              )}
            </span>
          </div>

          {libraryItems.length > 0 ? (
            <div
              className="space-y-2"
              data-testid="studio-library-card-grid"
            >
              {libraryItems.map((item) => {
                const active = selectedItem?.workItem.id === item.workItem.id
                const sourceProjectId = sourceProjectIdByItem.get(item.workItem.id)
                const sourceProject = sourceProjectId
                  ? projectById.get(sourceProjectId)
                  : undefined
                return (
                  <button
                    key={item.workItem.id}
                    type="button"
                    onClick={() => focusEvidence(item.workItem.id)}
                    data-testid="studio-library-card"
                    aria-pressed={active}
                    className={`flex w-full items-center gap-3 rounded-xl border bg-card p-3 text-left transition-all ${
                      active
                        ? 'border-foreground/40 bg-muted/40 ring-2 ring-foreground/10'
                        : 'border-border hover:border-foreground/20'
                    }`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                      <LibraryBig className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground">
                        {itemTitle(item)}
                      </span>
                      <span className="mt-1 block truncate text-xs text-muted-foreground">
                        {sourceLabel(item.workItem.sourceType)} ·{' '}
                        {itemTargetLabel(item)} ·{' '}
                        {new Date(item.updatedAt).toLocaleDateString('ko-KR')}
                      </span>
                      {projectRelationshipsReady ? (
                        <span className="mt-1 block truncate text-[11px] text-muted-foreground">
                          {sourceProject
                            ? relationshipCopy.sourceWorkspace(sourceProject.name)
                            : relationshipCopy.sourceUnavailable}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${proofClass(item.proofLevel)}`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${item.proofLevel === 'failed' ? 'bg-destructive' : 'bg-muted-foreground'}`}
                        aria-hidden
                      />
                      {libraryStatusLabel(item.proofLevel)}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            // FINDING-005: the empty state used to carry a placeholder
            // thumbnail and three mock chips ("Open link" / "Source note" /
            // "Launch check") — dead chrome previewing UI the user can't
            // touch yet. One icon, one sentence, one action.
            <div
              className="rounded-2xl border border-dashed border-border bg-card p-6 text-center sm:p-10"
              data-testid="library-agent-first-empty"
            >
              <PackageCheck className="mx-auto h-9 w-9 text-muted-foreground/50" />
              <h2 className="mt-3 text-base font-semibold text-foreground">
                {libraryCopy.empty.title}
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                {libraryCopy.empty.body}
              </p>
              {projectRelationshipsReady ? (
                <div className="mx-auto mt-5 flex max-w-lg flex-wrap items-center justify-center gap-2 rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground">
                  <FolderOpen className="h-4 w-4" aria-hidden />
                  <span>{relationshipCopy.emptyCounts(userProjects.length)}</span>
                </div>
              ) : null}
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={goToProjects}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <FolderOpen className="h-4 w-4" />
                  {strings.navigation.work}
                </button>
                <button
                  type="button"
                  onClick={goToImport}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-foreground px-4 text-sm font-semibold text-background transition-colors hover:opacity-90"
                >
                  <Sparkles className="h-4 w-4" />
                  {libraryCopy.importSource}
                </button>
              </div>
            </div>
          )}
        </div>

        <aside
          id="library-details-panel"
          aria-hidden={!selectedItem}
          className={
            selectedItem
              ? 'rounded-xl border border-border bg-card p-5 shadow-sm'
              : 'hidden'
          }
        >
          {selectedItem ? (
            <div className="space-y-5">
              <div className="space-y-4">
                <AppThumbnailPreview
                  title={itemTitle(selectedItem)}
                  imageSrc={selectedItem.preview?.previewUrl ? undefined : null}
                  kind="library"
                />
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-foreground">
                        {itemTitle(selectedItem)}
                      </h2>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${proofClass(selectedItem.proofLevel)}`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${selectedItem.proofLevel === 'failed' ? 'bg-destructive' : 'bg-muted-foreground'}`}
                          aria-hidden
                        />
                        {libraryStatusLabel(selectedItem.proofLevel)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      {itemTargetLabel(selectedItem)} ·{' '}
                      {sourceLabel(selectedItem.workItem.sourceType)}
                    </p>
                    {projectRelationshipsReady ? (
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {(() => {
                          const sourceProjectId = sourceProjectIdByItem.get(selectedItem.workItem.id)
                          const sourceProject = sourceProjectId
                            ? projectById.get(sourceProjectId)
                            : undefined
                          return sourceProject
                            ? relationshipCopy.sourceWorkspace(sourceProject.name)
                            : relationshipCopy.sourceUnavailable
                        })()}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {selectedItem.preview?.previewUrl ? (
                    <a
                      href={selectedItem.preview.previewUrl}
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-foreground px-4 text-sm font-semibold text-background transition-opacity hover:opacity-90"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {libraryCopy.actions.open.label}
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      aria-describedby="library-selected-open-disabled-reason"
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-muted px-4 text-sm font-semibold text-muted-foreground opacity-60"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {libraryCopy.actions.open.label}
                    </button>
                  )}
                  <AskCogoSurfaceButton
                    label={strings.report.askCoogo}
                    operationLabel="Improve or reuse library item"
                    operationKind="library-item"
                    keepVisibleWhenChatOpen
                    context={() =>
                      [
                        `Selected Library item: ${itemTitle(selectedItem)}`,
                        `Work item id: ${selectedItem.workItem.id}`,
                        `Source: ${sourceLabel(selectedItem.workItem.sourceType)} (${selectedItem.workItem.sourceRef.label})`,
                        `Type: ${itemTargetLabel(selectedItem)}`,
                        `Status: ${libraryStatusLabel(selectedItem.proofLevel)}`,
                        `Saved bundle: ${selectedItem.bundle?.id ?? 'missing'}`,
                        `Open URL: ${selectedItem.preview?.previewUrl ?? 'missing'}`,
                        'Help the user improve this saved work or reuse it as the starting point for a new result. Ask which outcome they want before changing anything.',
                      ].join('\n')
                    }
                    chatContextPatch={{
                      // The chat adapter sends the currently loaded project's
                      // bounded app payload. Keep its project identity aligned
                      // with that payload; the selected Library item remains a
                      // referenced source in the operation context below.
                      projectId: currentProjectId ?? selectedItem.workItem.id,
                      sourceWorkspaceId: selectedItem.workItem.workspaceId,
                      sourceRevision: selectedItem.bundle?.id,
                    }}
                    className="h-11 w-full justify-center border-foreground/20 px-4 text-sm"
                  />
                </div>
                {!selectedItem.preview?.previewUrl ? (
                  <span
                    id="library-selected-open-disabled-reason"
                    className="sr-only"
                  >
                    {libraryCopy.actions.open.noLink}
                  </span>
                ) : null}
                {!libraryItemWorkspacePath(selectedItem) ? (
                  <span
                    id="library-selected-edit-disabled-reason"
                    className="sr-only"
                  >
                    {libraryCopy.actions.edit.noWorkspace}
                  </span>
                ) : null}
                {!selectedItem.bundle?.id ? (
                  <span
                    id="library-selected-launch-disabled-reason"
                    className="sr-only"
                  >
                    {libraryCopy.actions.launch.noBundle}
                  </span>
                ) : null}
                {busyTool ? (
                  <span
                    id="library-selected-busy-action-reason"
                    className="sr-only"
                  >
                    {libraryCopy.actions.launch.busy.replace('{tool}', busyTool)}
                  </span>
                ) : null}
              </div>

              <details
                className="group rounded-xl border border-border bg-background"
                data-testid="library-selected-item-details"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground marker:content-none">
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                    Details
                  </span>
                  <span className="text-xs font-normal text-muted-foreground group-open:hidden">
                    {libraryCopy.selectedDetails.summary}
                  </span>
                  <span className="hidden text-xs font-normal text-muted-foreground group-open:inline">
                    Hide
                  </span>
                </summary>
                <div className="space-y-5 border-t border-border p-4">
              <div className="grid gap-2 text-xs">
                <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium text-foreground">
                    {itemTargetLabel(selectedItem)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
                  <span className="text-muted-foreground">Source</span>
                  <span className="font-medium text-foreground">
                    {sourceLabel(selectedItem.workItem.sourceType)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
                  <span className="text-muted-foreground">Saved file</span>
                  <span className="font-medium text-foreground">
                    {selectedItem.bundle?.validationStatus ?? 'not run'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
                  <span className="text-muted-foreground">Open link</span>
                  <span className="font-medium text-foreground">
                    {selectedItem.preview?.status ?? 'missing'}
                  </span>
                </div>
              </div>

              {!selectedHasEmbeddedReviewPayload &&
              selectedItem.preview?.previewUrl ? (
                <p
                  id="library-selected-review-disabled-reason"
                  className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs leading-5 text-muted-foreground"
                  data-testid="library-review-share-payload-warning"
                >
                  {libraryCopy.selectedDetails.openBeforeReviewLink}
                </p>
              ) : null}

              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Selected item quality
                </h3>
                <div
                  className="grid gap-2 sm:grid-cols-3"
                  data-testid="studio-library-card-quality"
                >
                  {selectedQualityItems.map((quality) => (
                    <div
                      key={quality.label}
                      className={`rounded-lg border px-3 py-2 text-xs ${readinessClass(quality.state)}`}
                    >
                      <span className="flex items-center gap-1.5 font-semibold">
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${readinessDotClass(quality.state)}`}
                          aria-hidden
                        />
                        {quality.label}
                      </span>
                      <span className="mt-1 block truncate opacity-80">
                        {quality.value}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section
                className="grid gap-2 sm:grid-cols-4"
                aria-label={strings.a11y.librarySummary}
              >
                {[
                  [libraryCopy.tiles.savedWork, counts.items],
                  [libraryCopy.tiles.openLinks, counts.previews],
                  [libraryCopy.tiles.details, counts.evidence],
                  [libraryCopy.tiles.launches, counts.deployments],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-border bg-card px-3 py-2"
                  >
                    <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {label}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-foreground">
                      {value}
                    </div>
                  </div>
                ))}
              </section>

              <section
                className="space-y-3 rounded-xl border border-border bg-background p-3"
                data-testid="studio-library-capability"
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {libraryCopy.whatYouCanDo}
                  </h3>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {itemTargetLabel(selectedItem)}
                  </span>
                </div>
                <div className="grid gap-2">
                  {selectedCapabilities.map((capability) => (
                    <div
                      key={capability.label}
                      className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-foreground">
                          {capability.label}
                        </div>
                        <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                          {capability.detail}
                        </div>
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${readinessClass(capability.state)}`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${readinessDotClass(capability.state)}`}
                          aria-hidden
                        />
                        {capability.state}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section
                className="space-y-3 rounded-xl border border-border bg-background p-3"
                data-testid="studio-readiness"
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {libraryCopy.readyToUse}
                  </h3>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {itemTargetLabel(selectedItem)}
                  </span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {readinessItems.map((item) => (
                    <div
                      key={item.label}
                      className="min-w-0 rounded-lg border border-border bg-card px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          {item.label}
                        </span>
                        <span
                          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${readinessClass(item.state)}`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${readinessDotClass(item.state)}`}
                            aria-hidden
                          />
                          {item.state}
                        </span>
                      </div>
                      <div className="mt-1 truncate text-xs font-semibold text-foreground">
                        {item.value}
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-muted-foreground">
                        {item.detail}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!libraryItemWorkspacePath(selectedItem)}
                  title={
                    libraryItemWorkspacePath(selectedItem)
                      ? undefined
                      : libraryCopy.actions.edit.noWorkspace
                  }
                  aria-describedby={
                    libraryItemWorkspacePath(selectedItem)
                      ? undefined
                      : 'library-selected-edit-disabled-reason'
                  }
                  onClick={() => openItemWorkspace(selectedItem)}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  {libraryCopy.actions.edit.label}
                </button>
                <button
                  type="button"
                  onClick={() => startRemixFromCard(selectedItem)}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {libraryCopy.actions.useAgain}
                </button>
                <button
                  type="button"
                  disabled={!selectedItem.preview?.previewUrl || Boolean(busyTool)}
                  aria-describedby={
                    busyTool
                      ? 'library-selected-busy-action-reason'
                      : !selectedItem.preview?.previewUrl
                        ? 'library-selected-open-disabled-reason'
                        : undefined
                  }
                  onClick={() => publishGalleryFromCard(selectedItem)}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {libraryCopy.actions.sharePublicly}
                </button>
                <button
                  type="button"
                  disabled={
                    !selectedItem.preview?.previewUrl || Boolean(busyTool)
                  }
                  aria-describedby={
                    busyTool
                      ? 'library-selected-busy-action-reason'
                      : !selectedItem.preview?.previewUrl
                        ? 'library-selected-open-disabled-reason'
                        : undefined
                  }
                  onClick={() =>
                    selectedItem.preview &&
                    runLibraryTool(
                      libraryCopy.actions.check,
                      'cogo.studio.inspect_cogo_preview',
                      {
                        previewUrl: selectedItem.preview.previewUrl,
                        checks: ['screenshot', 'console', 'desktop'],
                      },
                      libraryCopy.actions.feedback.check,
                    )
                  }
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {libraryCopy.actions.check}
                </button>
                <button
                  type="button"
                  disabled={
                    !canCreateReviewShare(
                      selectedItem,
                      currentProjectId,
                      currentProject as LoadedApp | null,
                      currentPreviewPayload,
                    ) || Boolean(busyTool)
                  }
                  aria-describedby={
                    busyTool
                      ? 'library-selected-busy-action-reason'
                      : !selectedItem.preview?.previewUrl
                        ? 'library-selected-open-disabled-reason'
                        : !selectedHasEmbeddedReviewPayload
                          ? 'library-selected-review-disabled-reason'
                          : undefined
                  }
                  onClick={() => void createReviewShareLink(selectedItem)}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {libraryCopy.actions.share}
                </button>
                <button
                  type="button"
                  disabled={!selectedItem.bundle?.id || Boolean(busyTool)}
                  aria-describedby={
                    busyTool
                      ? 'library-selected-busy-action-reason'
                      : !selectedItem.bundle?.id
                        ? 'library-selected-launch-disabled-reason'
                        : undefined
                  }
                  onClick={() => prepareDeployFromCard(selectedItem)}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Rocket className="h-3.5 w-3.5" />
                  {libraryCopy.actions.prepareLaunch}
                </button>
              </div>

              <div
                className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
                role="status"
              >
                {busyTool && (
                  <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                )}
                {message}
              </div>

              <section
                className="space-y-3 rounded-xl border border-border bg-background p-3"
                data-testid="library-review-inbox"
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Review inbox
                  </h3>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    {selectedReviewItems.length} items
                  </span>
                </div>
                <div
                  className="space-y-2 rounded-lg border border-border bg-card p-3"
                  data-testid="library-review-share-management"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                        Review links
                      </div>
                      <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                        Share tokens are not stored after creation. Create a
                        fresh link when a reviewer needs another copy.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={
                        !canCreateReviewShare(
                          selectedItem,
                          currentProjectId,
                          currentProject as LoadedApp | null,
                          currentPreviewPayload,
                        ) || Boolean(busyTool)
                      }
                      aria-describedby={
                        busyTool
                          ? 'library-selected-busy-action-reason'
                          : !selectedItem.preview?.previewUrl
                            ? 'library-selected-open-disabled-reason'
                            : !selectedHasEmbeddedReviewPayload
                              ? 'library-selected-review-disabled-reason'
                              : undefined
                      }
                      onClick={() => void createReviewShareLink(selectedItem)}
                      className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-border px-2.5 text-[11px] font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Create suggest link
                    </button>
                  </div>
                  {selectedReviewShares.length === 0 ? (
                    <p className="rounded-md bg-muted/50 px-2 py-1.5 text-[11px] leading-4 text-muted-foreground">
                      {libraryCopy.noReviewLinks}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {selectedReviewShares
                        .slice()
                        .reverse()
                        .map((share) => {
                          const state = reviewShareState(share)
                          const active = state === 'active'
                          return (
                            <div
                              key={share.id}
                              className="rounded-lg border border-border bg-background p-2"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span
                                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${reviewShareStateClass(share)}`}
                                    >
                                      {state}
                                    </span>
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                      {share.visibility}
                                    </span>
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                      #{shortTokenHash(share)}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                                    Created {shortDate(share.createdAt)}
                                    {share.expiresAt
                                      ? ` · expires ${shortDate(share.expiresAt)}`
                                      : ''}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <select
                                    aria-label={`Review link role ${shortTokenHash(share)}`}
                                    value={share.role}
                                    disabled={!active || Boolean(busyTool)}
                                    onChange={(event) =>
                                      void updateReviewShareRole(
                                        share,
                                        event.target.value as PreviewShareRole,
                                      )
                                    }
                                    className="h-8 rounded-md border border-border bg-card px-2 text-[11px] font-medium text-foreground outline-none focus:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {reviewShareRoleOptions(share).map(
                                      (role) => (
                                        <option key={role} value={role}>
                                          {role}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                  <button
                                    type="button"
                                    disabled={!active || Boolean(busyTool)}
                                    onClick={() =>
                                      void revokeReviewShare(share)
                                    }
                                    className="inline-flex h-8 items-center justify-center rounded-md border border-border px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <XCircle className="mr-1 h-3.5 w-3.5" />
                                    Revoke
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>
                {selectedReviewItems.length === 0 ? (
                  <p className="text-xs leading-5 text-muted-foreground">
                    Create a review link, share it, then client comments and
                    suggestions will appear here.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {selectedReviewItems
                      .slice()
                      .reverse()
                      .map((item) => (
                        <div
                          key={item.id}
                          className="rounded-lg border border-border bg-card p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                  {item.kind}
                                </span>
                                <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                  {item.status}
                                </span>
                              </div>
                              <p className="mt-2 text-xs leading-5 text-foreground">
                                {item.body}
                              </p>
                              <p className="mt-1 text-[11px] text-muted-foreground">
                                {item.targetRef.label ??
                                  item.targetRef.nodeId ??
                                  item.targetRef.blockId ??
                                  item.targetRef.path ??
                                  'Selected element'}
                              </p>
                              {item.proposedPatch?.op === 'replaceText' && (
                                <p className="mt-2 rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                                  Suggested text:{' '}
                                  <span className="font-medium text-foreground">
                                    {item.proposedPatch.nextText}
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                          {item.status === 'open' && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {item.proposedPatch ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => void acceptReviewItem(item)}
                                    className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-foreground px-3 text-xs font-medium text-background"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void rejectReviewItem(item)}
                                    className="inline-flex h-8 items-center justify-center rounded-md border border-border px-3 text-xs font-medium text-foreground"
                                  >
                                    Reject
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => void resolveReviewItem(item)}
                                  className="inline-flex h-8 items-center justify-center rounded-md border border-border px-3 text-xs font-medium text-foreground"
                                >
                                  Resolve
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
                {selectedAcceptedChangeSets.length > 0 && (
                  <div className="space-y-2 border-t border-border pt-3">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Accepted changes
                    </h4>
                    {selectedAcceptedChangeSets
                      .slice()
                      .reverse()
                      .map((changeSet) => (
                        <div
                          key={changeSet.id}
                          className="rounded-lg border border-border bg-card p-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-foreground">
                                {changeSet.appliedVersionId}
                              </div>
                              <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
                                {changeSet.patches.length} change
                                {changeSet.patches.length === 1 ? '' : 's'}{' '}
                                accepted · restore point{' '}
                                {changeSet.rollbackVersionId}
                              </p>
                              {changeSet.rolledBackAt && (
                                <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
                                  Rolled back into{' '}
                                  {changeSet.rollbackAppliedVersionId}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={
                                Boolean(changeSet.rolledBackAt) ||
                                !changeSet.rollbackPatches?.length
                              }
                              onClick={() =>
                                void rollbackAcceptedChangeSet(changeSet)
                              }
                              className="inline-flex h-8 items-center justify-center rounded-md border border-border px-3 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Restore
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </section>

              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Recent details
                </h3>
                {(packet?.evidence ?? [])
                  .slice(-5)
                  .reverse()
                  .map((artifact) => (
                    <div
                      key={artifact.id}
                      className="rounded-lg border border-border bg-background p-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-medium text-foreground">
                          {artifact.type}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {artifact.status}
                        </span>
                      </div>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {artifact.summary}
                      </p>
                    </div>
                  ))}
                {deployments.length > 0 && (
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                    {deployments.length} launch record
                    {deployments.length === 1 ? '' : 's'} saved.
                  </div>
                )}
              </div>
                </div>
              </details>
            </div>
          ) : (
            <div className="flex min-h-[420px] flex-col justify-center gap-4 text-center">
              <AppThumbnailPreview
                title={libraryCopy.previewTitle}
                imageSrc={null}
                kind="library"
              />
              <Archive className="mx-auto h-9 w-9 text-muted-foreground/40" />
              <h2 className="mt-4 text-sm font-semibold text-foreground">
                {libraryCopy.previewEmptyTitle}
              </h2>
              <p className="mt-2 max-w-xs text-sm leading-6 text-muted-foreground">
                {libraryCopy.previewEmptyBody}
              </p>
            </div>
          )}
        </aside>
      </section>
    </DashboardContainer>
  )
}
